import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import useAuth from "./useAuth";
import useYardDetailQuery from "./useYardQuery";
import { switchActiveYard } from "../utils/yard-switching/switchActiveYard";
import useMyYardsQuery from "../SwitchActiveYardFromURL/hooks/useMyYardsQuery";

/**
 * Global hook that checks for ?activeYard=123 query parameter
 * and switches the active yard if present. Removes the param after switching.
 *
 * This enables deep linking with automatic yard context switching,
 * e.g., /photos/manage/456?activeYard=789
 */
const useSetActiveYardFromQueryParam = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { loggedInUser, update, isLoggedIn } = useAuth();
  const { yardQuery } = useYardDetailQuery();
  const { myYardsQuery } = useMyYardsQuery();
  const isProcessingRef = useRef(false);

  useEffect(() => {
    const activeYardParam = searchParams.get("activeYard");

    if (!activeYardParam || !isLoggedIn || isProcessingRef.current || !loggedInUser) {
      return;
    }

    // Set guard synchronously before async work to prevent concurrent calls
    isProcessingRef.current = true;

    const performSwitch = async () => {
      try {
        await switchActiveYard({
          yardId: activeYardParam,
          isAdmin: loggedInUser.isAdmin || false,
          yardQuery,
          myYardsQuery,
          updateAuth: update,
        });

        const newParams = new URLSearchParams(searchParams);
        newParams.delete("activeYard");
        setSearchParams(newParams, { replace: true });
      } catch (error) {
        console.error("Failed to switch active yard from query param:", error);
      } finally {
        isProcessingRef.current = false;
      }
    };

    performSwitch();
  }, [searchParams, isLoggedIn, loggedInUser?.id]);
};

export default useSetActiveYardFromQueryParam;
